import { useState, useEffect, useRef } from 'react'
import { useAuth } from '../contexts/AuthContext'
import api from '../utils/api'

const TYPE_COLORS = {
  policy:   { bg: '#8B1A1A', color: '#f0d0d0' },
  analysis: { bg: '#5c3a1e', color: '#f0d9c0' },
  report:   { bg: '#1a4a6e', color: '#c0d8f0' },
  brief:    { bg: '#2d4a1a', color: '#c8e0c0' },
}

const S = {
  container: { display: 'flex', height: 'calc(100vh - 4rem)', fontFamily: "'DM Sans', sans-serif", gap: '1.5rem' },
  sidebar: {
    width: '300px', minWidth: '300px', overflowY: 'auto',
    display: 'flex', flexDirection: 'column', gap: '0.75rem',
  },
  sidebarHeader: { marginBottom: '0.25rem' },
  sidebarTitle: { fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.14em', color: '#bbb', fontWeight: 600 },
  docCard: (active) => ({
    background: active ? 'rgba(139,26,26,0.1)' : '#111',
    border: `1px solid ${active ? '#8B1A1A' : '#1c1c1c'}`,
    borderRadius: '8px', padding: '1rem', cursor: 'pointer',
    transition: 'border-color 0.15s, background 0.15s',
  }),
  docCardMeta: { display: 'flex', alignItems: 'center', gap: '0.4rem', marginBottom: '0.5rem', flexWrap: 'wrap' },
  typeBadge: (type) => ({
    padding: '0.12rem 0.5rem', borderRadius: '3px', fontSize: '0.65rem', fontWeight: 700,
    textTransform: 'uppercase', letterSpacing: '0.07em',
    background: TYPE_COLORS[type]?.bg || '#2a1a1a',
    color: TYPE_COLORS[type]?.color || '#f0c0c0',
  }),
  featuredBadge: {
    padding: '0.12rem 0.5rem', borderRadius: '3px', fontSize: '0.65rem', fontWeight: 700,
    textTransform: 'uppercase', letterSpacing: '0.07em',
    background: '#B8860B', color: '#1a1200',
  },
  docCardTitle: { fontSize: '0.85rem', fontWeight: 600, color: '#d0d0d0', lineHeight: 1.4, margin: '0 0 0.4rem 0' },
  docCardDesc: { fontSize: '0.72rem', color: '#d0d0d0', lineHeight: 1.5, margin: 0 },
  docCardDate: { fontSize: '0.68rem', color: '#bbb', marginTop: '0.4rem' },
  viewBtn: (active) => ({
    marginTop: '0.6rem', padding: '0.35rem 1rem', borderRadius: '4px', fontSize: '0.75rem',
    fontWeight: 600, letterSpacing: '0.08em', textTransform: 'uppercase',
    background: active ? '#8B1A1A' : 'transparent',
    border: `1px solid ${active ? '#8B1A1A' : '#2a2a2a'}`,
    color: active ? '#fff' : '#aaa', cursor: 'pointer', transition: 'all 0.15s',
  }),
  main: { flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' },
  viewerWrap: { flex: 1, position: 'relative', overflow: 'hidden', borderRadius: '8px', border: '1px solid #1c1c1c' },
  iframe: { width: '100%', height: 'calc(100vh - 120px)', border: 'none', display: 'block' },
  watermarkOverlay: {
    position: 'absolute', top: 0, left: 0, right: 0, bottom: 0,
    pointerEvents: 'none', overflow: 'hidden', zIndex: 10,
  },
  empty: {
    flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center',
    color: '#bbb', fontSize: '0.9rem', fontStyle: 'italic',
    flexDirection: 'column', gap: '0.5rem',
  },
  loadingWrap: {
    flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#bbb', fontSize: '0.85rem',
  },
  skeletonCard: {
    background: '#111', border: '1px solid #1c1c1c', borderRadius: '8px',
    padding: '1rem', display: 'flex', flexDirection: 'column', gap: '0.5rem',
  },
  skeletonLine: (w, h) => ({
    background: '#1a1a1a', borderRadius: '4px', height: h || '12px', width: w || '100%',
    animation: 'pulse 1.4s ease-in-out infinite',
  }),
}

function formatDate(str) {
  if (!str) return ''
  try {
    return new Date(str).toLocaleDateString('en-US', { year: 'numeric', month: 'long' })
  } catch { return '' }
}

function WatermarkOverlay({ email }) {
  const canvasRef = useRef(null)

  useEffect(() => {
    if (!canvasRef.current || !email) return
    const canvas = canvasRef.current
    const ctx = canvas.getContext('2d')
    const W = canvas.width
    const H = canvas.height

    ctx.clearRect(0, 0, W, H)
    ctx.save()
    ctx.globalAlpha = 0.045
    ctx.fillStyle = '#c0a060'
    ctx.font = '13px DM Sans, sans-serif'
    ctx.rotate(-Math.PI / 6)

    const text = `CONFIDENTIAL — ${email}`
    const stepX = 320
    const stepY = 140

    for (let y = -H; y < H * 2; y += stepY) {
      for (let x = -W; x < W * 2; x += stepX) {
        ctx.fillText(text, x, y)
      }
    }
    ctx.restore()
  }, [email])

  return (
    <canvas
      ref={canvasRef}
      width={1200}
      height={1600}
      style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', pointerEvents: 'none' }}
    />
  )
}

function DocCard({ doc, active, onSelect }) {
  return (
    <div style={S.docCard(active)} onClick={() => onSelect(doc)}>
      <div style={S.docCardMeta}>
        <span style={S.typeBadge(doc.content_type)}>{doc.content_type}</span>
        {doc.is_featured && <span style={S.featuredBadge}>Featured</span>}
      </div>
      <p style={S.docCardTitle}>{doc.title}</p>
      {doc.description && <p style={S.docCardDesc}>{doc.description}</p>}
      <div style={S.docCardDate}>{formatDate(doc.published_at)}</div>
      <button style={S.viewBtn(active)}>View</button>
    </div>
  )
}

export default function PolicyDocuments() {
  const { user } = useAuth()
  const [documents, setDocuments] = useState([])
  const [loading, setLoading] = useState(true)
  const [selected, setSelected] = useState(null)
  const [docContent, setDocContent] = useState(null)
  const [loadingDoc, setLoadingDoc] = useState(false)

  useEffect(() => {
    api.get('/data/documents')
      .then(res => {
        setDocuments(res.data.documents)
        // auto-select featured or first
        const featured = res.data.documents.find(d => d.is_featured) || res.data.documents[0]
        if (featured) handleSelect(featured)
      })
      .catch(err => console.error('documents fetch error:', err))
      .finally(() => setLoading(false))
  }, [])

  const handleSelect = async (doc) => {
    if (selected?.id === doc.id) return
    setSelected(doc)
    setDocContent(null)
    setLoadingDoc(true)
    try {
      const res = await api.get(`/data/documents/${doc.id}`)
      setDocContent(res.data.document.content_html)
    } catch (err) {
      console.error('document load error:', err)
      setDocContent('<p style="color:#888;padding:2rem;">Failed to load document.</p>')
    } finally {
      setLoadingDoc(false)
    }
  }

  const injectWatermark = (html, email) => {
    if (!html || !email) return html
    const watermarkStyle = `
      <style>
        body::after {
          content: '';
          position: fixed;
          top: 0; left: 0; right: 0; bottom: 0;
          pointer-events: none;
          z-index: 9999;
          background-image: repeating-linear-gradient(
            -30deg,
            transparent,
            transparent 60px,
            rgba(192,160,96,0.04) 60px,
            rgba(192,160,96,0.04) 61px
          );
        }
        @media print {
          body { display: none !important; }
        }
      </style>
      <div id="mp-wm" style="
        position:fixed;top:0;left:0;right:0;bottom:0;
        pointer-events:none;z-index:9998;overflow:hidden;
      ">
        <canvas id="mp-wm-canvas" style="width:100%;height:100%;"></canvas>
      </div>
      <script>
        (function(){
          var c=document.getElementById('mp-wm-canvas');
          if(!c)return;
          c.width=window.innerWidth||1200;
          c.height=window.innerHeight||900;
          var ctx=c.getContext('2d');
          ctx.globalAlpha=0.04;
          ctx.fillStyle='#c0a060';
          ctx.font='12px Arial,sans-serif';
          ctx.rotate(-Math.PI/6);
          var text='CONFIDENTIAL \u2014 ${email}';
          for(var y=-c.height;y<c.height*2;y+=130){
            for(var x=-c.width;x<c.width*2;x+=310){
              ctx.fillText(text,x,y);
            }
          }
        })();
      </script>
    `
    // inject just before </body> or at the end
    if (html.includes('</body>')) {
      return html.replace('</body>', watermarkStyle + '</body>')
    }
    return html + watermarkStyle
  }

  const srcdoc = docContent && user?.email
    ? injectWatermark(docContent, user.email)
    : docContent

  return (
    <div style={S.container}>
      <style>{`
        @keyframes pulse { 0%,100%{opacity:1} 50%{opacity:0.4} }
        .doc-card:hover { border-color: #2a2a2a !important; }
      `}</style>

      {/* Sidebar */}
      <div style={S.sidebar}>
        <div style={S.sidebarHeader}>
          <div style={S.sidebarTitle}>Policy Documents</div>
        </div>

        {loading ? (
          Array.from({ length: 3 }).map((_, i) => (
            <div key={i} style={S.skeletonCard}>
              <div style={{ display: 'flex', gap: '0.4rem' }}>
                <div style={S.skeletonLine('60px', '16px')} />
                <div style={S.skeletonLine('70px', '16px')} />
              </div>
              <div style={S.skeletonLine('90%', '14px')} />
              <div style={S.skeletonLine('75%', '14px')} />
              <div style={S.skeletonLine('50%', '11px')} />
              <div style={S.skeletonLine('80px', '28px')} />
            </div>
          ))
        ) : documents.length === 0 ? (
          <div style={{ color: '#bbb', fontSize: '0.8rem', fontStyle: 'italic', padding: '1rem 0' }}>
            No documents available.
          </div>
        ) : (
          documents.map(doc => (
            <DocCard
              key={doc.id}
              doc={doc}
              active={selected?.id === doc.id}
              onSelect={handleSelect}
            />
          ))
        )}
      </div>

      {/* Main viewer */}
      <div style={S.main}>
        {!selected && !loading ? (
          <div style={S.empty}>
            <span style={{ fontSize: '2rem', opacity: 0.15 }}>&#128196;</span>
            <span>Select a document to view</span>
          </div>
        ) : loadingDoc ? (
          <div style={S.loadingWrap}>Loading document…</div>
        ) : docContent ? (
          <div style={S.viewerWrap}>
            <iframe
              srcDoc={srcdoc}
              sandbox="allow-same-origin allow-scripts"
              style={S.iframe}
              title={selected?.title || 'Policy Document'}
            />
            <div style={S.watermarkOverlay}>
              <WatermarkOverlay email={user?.email || ''} />
            </div>
          </div>
        ) : null}
      </div>
    </div>
  )
}
